import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ExternalLink, type LucideIcon } from "lucide-react";
import { ModularizationDialog } from "./ModularizationDialog";
import { ConstructionMethodsDialog } from "./ConstructionMethodsDialog";
import { DomainButton } from "./DomainButton";
import EngineeringContent from "./EngineeringContent";

interface ChannelCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  details: string;
  example: string;
  featured?: {
    name: string;
    logo: string;
    link: string;
  }[];
}

export const ChannelCard = ({ 
  title, 
  description, 
  icon: Icon, 
  details, 
  example, 
  featured 
}: ChannelCardProps) => {
  return (
    <Card 
      className="border-2 border-black flex flex-col"
      id={title.toLowerCase().replace(' channels', '')}
    >
      <CardHeader className="flex-grow-0">
        <div className="flex items-center gap-4 mb-2">
          <Icon className="h-6 w-6 text-black shrink-0 transition-transform duration-200 hover:scale-125" />
          {featured && (
            <div className="flex gap-2 flex-wrap">
              {featured.map((item) => (
                item.link.startsWith('http') ? (
                  <a 
                    key={item.name}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`transition-transform duration-200 hover:scale-[3] ${
                      title === "Semi-Private Channels" 
                        ? "h-12 w-12" 
                        : "h-8 w-8"
                    } flex items-center justify-center`}
                    aria-label={item.name}
                  >
                    <img 
                      src={item.logo} 
                      alt={item.name}
                      className={`object-contain ${
                        title === "Semi-Private Channels" ? "h-36 w-36" : "h-6 w-6"
                      }`}
                    />
                  </a>
                ) : (
                  <Link 
                    key={item.name}
                    to={item.link}
                    className={`transition-transform duration-200 hover:scale-[3] ${
                      title === "Semi-Private Channels" 
                        ? "h-12 w-12" 
                        : "h-8 w-8"
                    } flex items-center justify-center`}
                    aria-label={item.name}
                  >
                    <img 
                      src={item.logo} 
                      alt={item.name}
                      className={`object-contain ${
                        title === "Semi-Private Channels" ? "h-36 w-36" : "h-6 w-6"
                      }`}
                    />
                  </Link>
                )
              ))}
            </div>
          )}
        </div>
        <div className="h-14 flex items-center">
          <CardTitle className="text-xl font-bold text-black transition-transform duration-200 hover:scale-105">{title}</CardTitle>
        </div>
        <CardDescription className="text-gray-600">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-grow">
        <div className="space-y-4">
          <div className="bg-gray-50 p-3 rounded-lg transition-transform duration-200 hover:scale-105">
            <p className="text-xs text-gray-600">
              <span className="font-semibold">Example:</span> {example}
            </p>
          </div>
          <Button 
            variant="outline" 
            className="w-full text-sm bg-primary text-white hover:bg-primary/90 hover:text-white hover:scale-105 transition-all duration-200"
          >
            Create Channel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};